/**
 * Institutional-site UI copy — message ids resolved by `@vello/i18n` against a locale catalog (DD-04: no copy
 * or locale literal in structure). This is the **neutral chrome + bootstrap** copy (the market switcher, the
 * "choose a market" / "not available yet" states, the language names) in the platform's base UI languages —
 * Portuguese, English, Spanish and Italian — picked by the resolved display locale's primary language subtag
 * (`Country != Locale`), falling back to en-US. The *served market's* marketing content is not here: it is a
 * country-pack concern injected at runtime (`SiteContent`). Catalog locale tags stay language-only (never a
 * region-specific `pt-BR`); country/currency render from data, never from this copy.
 */
import { translate, UI_MESSAGE_IDS, type MessageCatalog } from '@vello/i18n';
import { SITE_INNER_PAGES, type SiteInnerPage, type SitePageSlugs } from '../src/site-routes.js';

const EN: Readonly<Record<string, string>> = {
  [UI_MESSAGE_IDS.actionConfirm]: 'Confirm',
  [UI_MESSAGE_IDS.actionCancel]: 'Cancel',
  [UI_MESSAGE_IDS.stateLoading]: 'Loading…',

  'site.nav.market': 'Country',
  'site.nav.language': 'Language',
  'site.notAvailable.title': 'Vello isn’t here yet',
  'site.notAvailable.body': 'We haven’t launched in {country} yet. Choose a market where Vello operates to explore the app.',
  'site.notAvailable.browse': 'Choose a market',
  'site.footer.rights': 'Vello. To go, come back and keep moving.',
  'site.footer.deleteAccount': 'Delete my account',
  'site.footer.home': 'Home page',
  'site.route.accountDeletion': 'delete-account',
  'site.route.drivers': 'drivers',
  'site.route.business': 'business',
  'site.route.safety': 'safety',
  'site.route.help': 'help',
  'site.route.about': 'about',
  'site.route.press': 'press',
  'site.route.careers': 'careers',
  'site.route.signIn': 'sign-in',
  'site.route.download': 'download',
  'site.nav.menu': 'Menu',
  'site.nav.closeMenu': 'Close menu',

  'erase.title': 'Delete my Vello account',
  'erase.intro': 'You can ask for your account to be deleted here, without opening the app. To confirm the number is yours, we send a code to it.',
  'erase.deleted.title': 'What is deleted',
  'erase.deleted.account': 'Your account and profile, including your name and phone number',
  'erase.deleted.sessions': 'Your sessions and signed-in devices',
  'erase.deleted.chat': 'Trip chat messages',
  'erase.deleted.location': 'Your location history',
  'erase.deleted.saved': 'Saved data, such as favourite places and trusted contacts',
  'erase.kept.title': 'What is kept',
  'erase.kept.records': 'Trips and payments: kept pseudonymized (without your name or number) for the period the law requires.',
  'erase.kept.safety': 'Open safety cases: kept until they are closed.',
  'erase.phone.step': 'Confirm your number',
  'erase.code.step': 'Enter the code',
  'erase.phone.label': 'Mobile number',
  'erase.phone.hint': 'With the country code, starting with +.',
  'erase.phone.invalid': 'Check the number: it must start with + and the country code.',
  'erase.phone.submit': 'Send code',
  'erase.phone.sending': 'Sending…',
  'erase.code.sentTo': 'We sent a code to {destination}.',
  'erase.code.sent': 'We sent a code to the number you entered.',
  'erase.code.label': 'Code received',
  'erase.code.empty': 'Enter the code you received.',
  'erase.code.wrong': 'Wrong or expired code. Check it and try again.',
  'erase.code.submit': 'Confirm deletion',
  'erase.code.confirming': 'Confirming…',
  'erase.code.change': 'Use another number',
  'erase.done.title': 'Request received',
  'erase.done.body': 'We received your request. If a Vello account exists with this number, it will be deleted; records the law requires us to keep (such as trips and payments) stay pseudonymized for the legal period.',
  'erase.done.home': 'Back to the home page',
  'erase.error.rateLimited': 'Too many attempts. Please try again later.',
  'erase.error.challenge': 'We could not send the code right now. Please try again later.',
  'erase.error.notServed': 'We do not send codes to numbers from this country yet.',
  'erase.error.unavailable': 'This could not be completed right now. Please try again in a moment.',
  // D440: the public trip-share link and the legal texts.
  'site.trip.loading': 'Loading…',
  'site.trip.state.waiting': 'Looking for a driver',
  'site.trip.state.onTheWay': 'The driver is on the way to the pickup',
  'site.trip.state.arrived': 'The driver has arrived at the pickup',
  'site.trip.state.onTrip': 'On the trip',
  'site.trip.driver': 'Driver: {name}',
  'site.trip.mapLabel': 'Map with the last known position of the vehicle',
  'site.trip.mapUnavailable': 'The map is not available on this device. The state of the ride still updates here.',
  'site.trip.updatedAt': 'Updated at {time}',
  'site.trip.noPosition': 'The vehicle position is not available right now.',
  'site.trip.privacy': 'This link shows the vehicle, the road ahead and the destination. It does not show the name or phone of the person who asked for the trip, or the pickup address. It stops working when the trip ends.',
  'site.trip.unavailable': 'The trip could not be loaded right now. We will try again.',
  'site.trip.ended.notFound': 'This link does not exist.',
  'site.trip.ended.revoked': 'The rider stopped sharing this trip.',
  'site.trip.ended.expired': 'This link has expired.',
  'site.trip.ended.tripEnded': 'This trip has ended.',
  'site.trip.ended.cancelled': 'This ride was cancelled.',
  'site.trip.eta': 'Arriving in about {minutes} min',
  'site.legal.version': 'Version {version}',
  'site.legal.notPublished': 'This document has not been published yet.',
};

const PT: Readonly<Record<string, string>> = {
  [UI_MESSAGE_IDS.actionConfirm]: 'Confirmar',
  [UI_MESSAGE_IDS.actionCancel]: 'Cancelar',
  [UI_MESSAGE_IDS.stateLoading]: 'Carregando…',

  'site.nav.market': 'País',
  'site.nav.language': 'Idioma',
  'site.notAvailable.title': 'A Vello ainda não chegou aqui',
  'site.notAvailable.body': 'Ainda não lançamos em {country}. Escolha um mercado onde a Vello opera para conhecer o app.',
  'site.notAvailable.browse': 'Escolher um mercado',
  'site.footer.rights': 'Vello. Para ir, voltar e seguir em frente.',
  'site.footer.deleteAccount': 'Excluir minha conta',
  'site.footer.home': 'Página inicial',
  'site.route.accountDeletion': 'excluir-conta',
  'site.route.drivers': 'motorista',
  'site.route.business': 'empresas',
  'site.route.safety': 'seguranca',
  'site.route.help': 'ajuda',
  'site.route.about': 'sobre',
  'site.route.press': 'imprensa',
  'site.route.careers': 'carreiras',
  'site.route.signIn': 'entrar',
  'site.route.download': 'baixar',
  'site.nav.menu': 'Menu',
  'site.nav.closeMenu': 'Fechar menu',

  'erase.title': 'Excluir minha conta Vello',
  'erase.intro': 'Você pode pedir a exclusão da sua conta aqui, sem abrir o app. Para confirmar que o número é seu, enviamos um código para ele.',
  'erase.deleted.title': 'O que é excluído',
  'erase.deleted.account': 'Sua conta e seu perfil, incluindo nome e número de celular',
  'erase.deleted.sessions': 'Suas sessões e aparelhos conectados',
  'erase.deleted.chat': 'Mensagens do chat das corridas',
  'erase.deleted.location': 'Seu histórico de localização',
  'erase.deleted.saved': 'Dados salvos, como endereços favoritos e contatos de confiança',
  'erase.kept.title': 'O que fica guardado',
  'erase.kept.records': 'Corridas e pagamentos: ficam pseudonimizados (sem seu nome ou número) pelo prazo que a lei exige.',
  'erase.kept.safety': 'Casos de segurança em aberto: ficam guardados até serem encerrados.',
  'erase.phone.step': 'Confirme seu número',
  'erase.code.step': 'Digite o código',
  'erase.phone.label': 'Número de celular',
  'erase.phone.hint': 'Com o código do país, começando com +.',
  'erase.phone.invalid': 'Confira o número: ele precisa começar com + e o código do país.',
  'erase.phone.submit': 'Enviar código',
  'erase.phone.sending': 'Enviando…',
  'erase.code.sentTo': 'Enviamos um código para {destination}.',
  'erase.code.sent': 'Enviamos um código para o número informado.',
  'erase.code.label': 'Código recebido',
  'erase.code.empty': 'Digite o código que você recebeu.',
  'erase.code.wrong': 'Código incorreto ou expirado. Confira e tente de novo.',
  'erase.code.submit': 'Confirmar exclusão',
  'erase.code.confirming': 'Confirmando…',
  'erase.code.change': 'Usar outro número',
  'erase.done.title': 'Pedido recebido',
  'erase.done.body': 'Recebemos seu pedido. Se existir uma conta Vello com este número, ela será excluída; registros que a lei obriga a guardar (como corridas e pagamentos) ficam pseudonimizados pelo prazo legal.',
  'erase.done.home': 'Voltar à página inicial',
  'erase.error.rateLimited': 'Muitas tentativas. Tente novamente mais tarde.',
  'erase.error.challenge': 'Não foi possível enviar o código agora. Tente novamente mais tarde.',
  'erase.error.notServed': 'Ainda não enviamos códigos para números deste país.',
  'erase.error.unavailable': 'Não foi possível concluir agora. Tente novamente em instantes.',
  // D440: o link público da viagem e os documentos legais.
  'site.trip.loading': 'Carregando…',
  'site.trip.state.waiting': 'Procurando um motorista',
  'site.trip.state.onTheWay': 'O motorista está a caminho do embarque',
  'site.trip.state.arrived': 'O motorista chegou ao local de embarque',
  'site.trip.state.onTrip': 'Em viagem',
  'site.trip.driver': 'Motorista: {name}',
  'site.trip.mapLabel': 'Mapa com a última posição conhecida do veículo',
  'site.trip.mapUnavailable': 'O mapa não está disponível neste aparelho. O estado da viagem continua sendo atualizado aqui.',
  'site.trip.updatedAt': 'Atualizado às {time}',
  'site.trip.noPosition': 'A posição do veículo não está disponível agora.',
  'site.trip.privacy': 'Este link mostra o veículo, o trajeto e o destino. Não mostra o nome nem o telefone de quem pediu a viagem, nem o endereço de embarque. O link deixa de funcionar quando a viagem terminar.',
  'site.trip.unavailable': 'Não foi possível carregar a viagem agora. Vamos tentar de novo.',
  'site.trip.ended.notFound': 'Este link não existe.',
  'site.trip.ended.revoked': 'O passageiro parou de compartilhar esta viagem.',
  'site.trip.ended.expired': 'Este link expirou.',
  'site.trip.ended.tripEnded': 'Esta viagem já terminou.',
  'site.trip.ended.cancelled': 'Esta corrida foi cancelada.',
  'site.trip.eta': 'Chega em cerca de {minutes} min',
  'site.legal.version': 'Versão {version}',
  'site.legal.notPublished': 'Este documento ainda não foi publicado.',
};

const ES: Readonly<Record<string, string>> = {
  [UI_MESSAGE_IDS.actionConfirm]: 'Confirmar',
  [UI_MESSAGE_IDS.actionCancel]: 'Cancelar',
  [UI_MESSAGE_IDS.stateLoading]: 'Cargando…',

  'site.nav.market': 'País',
  'site.nav.language': 'Idioma',
  'site.notAvailable.title': 'Vello aún no llegó aquí',
  'site.notAvailable.body': 'Todavía no operamos en {country}. Elige un mercado donde Vello opera para conocer la app.',
  'site.notAvailable.browse': 'Elegir un mercado',
  'site.footer.rights': 'Vello. Para ir, volver y seguir adelante.',
  'site.footer.deleteAccount': 'Eliminar mi cuenta',
  'site.footer.home': 'Página de inicio',
  'site.route.accountDeletion': 'eliminar-cuenta',
  'site.route.drivers': 'conductores',
  'site.route.business': 'empresas',
  'site.route.safety': 'seguridad',
  'site.route.help': 'ayuda',
  'site.route.about': 'nosotros',
  'site.route.press': 'prensa',
  'site.route.careers': 'empleo',
  'site.route.signIn': 'acceder',
  'site.route.download': 'descargar',
  'site.nav.menu': 'Menú',
  'site.nav.closeMenu': 'Cerrar menú',

  'erase.title': 'Eliminar mi cuenta Vello',
  'erase.intro': 'Puedes pedir la eliminación de tu cuenta aquí, sin abrir la app. Para confirmar que el número es tuyo, te enviamos un código.',
  'erase.deleted.title': 'Qué se elimina',
  'erase.deleted.account': 'Tu cuenta y tu perfil, incluidos tu nombre y tu número de móvil',
  'erase.deleted.sessions': 'Tus sesiones y dispositivos conectados',
  'erase.deleted.chat': 'Los mensajes del chat de los viajes',
  'erase.deleted.location': 'Tu historial de ubicación',
  'erase.deleted.saved': 'Datos guardados, como direcciones favoritas y contactos de confianza',
  'erase.kept.title': 'Qué se conserva',
  'erase.kept.records': 'Viajes y pagos: se conservan seudonimizados (sin tu nombre ni tu número) durante el plazo que exige la ley.',
  'erase.kept.safety': 'Casos de seguridad abiertos: se conservan hasta que se cierran.',
  'erase.phone.step': 'Confirma tu número',
  'erase.code.step': 'Escribe el código',
  'erase.phone.label': 'Número de móvil',
  'erase.phone.hint': 'Con el código del país, empezando por +.',
  'erase.phone.invalid': 'Revisa el número: debe empezar por + y el código del país.',
  'erase.phone.submit': 'Enviar código',
  'erase.phone.sending': 'Enviando…',
  'erase.code.sentTo': 'Enviamos un código a {destination}.',
  'erase.code.sent': 'Enviamos un código al número indicado.',
  'erase.code.label': 'Código recibido',
  'erase.code.empty': 'Escribe el código que recibiste.',
  'erase.code.wrong': 'Código incorrecto o caducado. Revísalo e inténtalo de nuevo.',
  'erase.code.submit': 'Confirmar eliminación',
  'erase.code.confirming': 'Confirmando…',
  'erase.code.change': 'Usar otro número',
  'erase.done.title': 'Solicitud recibida',
  'erase.done.body': 'Recibimos tu solicitud. Si existe una cuenta Vello con este número, se eliminará; los registros que la ley obliga a conservar (como viajes y pagos) quedan seudonimizados durante el plazo legal.',
  'erase.done.home': 'Volver a la página de inicio',
  'erase.error.rateLimited': 'Demasiados intentos. Inténtalo de nuevo más tarde.',
  'erase.error.challenge': 'No pudimos enviar el código ahora. Inténtalo de nuevo más tarde.',
  'erase.error.notServed': 'Todavía no enviamos códigos a números de este país.',
  'erase.error.unavailable': 'No se pudo completar ahora. Inténtalo de nuevo en unos instantes.',
  // D440: el enlace público del viaje y los documentos legales.
  'site.trip.loading': 'Cargando…',
  'site.trip.state.waiting': 'Buscando un conductor',
  'site.trip.state.onTheWay': 'El conductor va camino al punto de recogida',
  'site.trip.state.arrived': 'El conductor llegó al punto de recogida',
  'site.trip.state.onTrip': 'En viaje',
  'site.trip.driver': 'Conductor: {name}',
  'site.trip.mapLabel': 'Mapa con la última posición conocida del vehículo',
  'site.trip.mapUnavailable': 'El mapa no está disponible en este dispositivo. El estado del viaje se sigue actualizando aquí.',
  'site.trip.updatedAt': 'Actualizado a las {time}',
  'site.trip.noPosition': 'La posición del vehículo no está disponible ahora.',
  'site.trip.privacy': 'Este enlace muestra el vehículo, el recorrido y el destino. No muestra el nombre ni el teléfono de quien pidió el viaje, ni la dirección de recogida. Deja de funcionar cuando el viaje termine.',
  'site.trip.unavailable': 'No se pudo cargar el viaje ahora. Lo intentaremos de nuevo.',
  'site.trip.ended.notFound': 'Este enlace no existe.',
  'site.trip.ended.revoked': 'El pasajero dejó de compartir este viaje.',
  'site.trip.ended.expired': 'Este enlace expiró.',
  'site.trip.ended.tripEnded': 'Este viaje ya terminó.',
  'site.trip.ended.cancelled': 'Este viaje fue cancelado.',
  'site.trip.eta': 'Llega en unos {minutes} min',
  'site.legal.version': 'Versión {version}',
  'site.legal.notPublished': 'Este documento aún no se ha publicado.',
};

const IT: Readonly<Record<string, string>> = {
  [UI_MESSAGE_IDS.actionConfirm]: 'Conferma',
  [UI_MESSAGE_IDS.actionCancel]: 'Annulla',
  [UI_MESSAGE_IDS.stateLoading]: 'Caricamento…',

  'site.nav.market': 'Paese',
  'site.nav.language': 'Lingua',
  'site.notAvailable.title': 'Vello non è ancora qui',
  'site.notAvailable.body': 'Non operiamo ancora in {country}. Scegli un mercato dove Vello opera per scoprire l’app.',
  'site.notAvailable.browse': 'Scegli un mercato',
  'site.footer.rights': 'Vello. Per andare, tornare e andare avanti.',
  'site.footer.deleteAccount': 'Elimina il mio account',
  'site.footer.home': 'Pagina iniziale',
  'site.route.accountDeletion': 'elimina-account',
  'site.route.drivers': 'autisti',
  'site.route.business': 'aziende',
  'site.route.safety': 'sicurezza',
  'site.route.help': 'aiuto',
  'site.route.about': 'chi-siamo',
  'site.route.press': 'stampa',
  'site.route.careers': 'lavora-con-noi',
  'site.route.signIn': 'accedi',
  'site.route.download': 'scarica',
  'site.nav.menu': 'Menu',
  'site.nav.closeMenu': 'Chiudi menu',

  'erase.title': 'Elimina il mio account Vello',
  'erase.intro': 'Puoi chiedere qui l’eliminazione del tuo account, senza aprire l’app. Per confermare che il numero è tuo, ti inviamo un codice.',
  'erase.deleted.title': 'Cosa viene eliminato',
  'erase.deleted.account': 'Il tuo account e il tuo profilo, compresi nome e numero di cellulare',
  'erase.deleted.sessions': 'Le tue sessioni e i dispositivi collegati',
  'erase.deleted.chat': 'I messaggi della chat delle corse',
  'erase.deleted.location': 'La cronologia delle posizioni',
  'erase.deleted.saved': 'I dati salvati, come indirizzi preferiti e contatti fidati',
  'erase.kept.title': 'Cosa viene conservato',
  'erase.kept.records': 'Corse e pagamenti: restano pseudonimizzati (senza nome né numero) per il periodo previsto dalla legge.',
  'erase.kept.safety': 'Casi di sicurezza aperti: conservati finché non vengono chiusi.',
  'erase.phone.step': 'Conferma il tuo numero',
  'erase.code.step': 'Inserisci il codice',
  'erase.phone.label': 'Numero di cellulare',
  'erase.phone.hint': 'Con il prefisso internazionale, iniziando con +.',
  'erase.phone.invalid': 'Controlla il numero: deve iniziare con + e il prefisso internazionale.',
  'erase.phone.submit': 'Invia codice',
  'erase.phone.sending': 'Invio in corso…',
  'erase.code.sentTo': 'Abbiamo inviato un codice a {destination}.',
  'erase.code.sent': 'Abbiamo inviato un codice al numero indicato.',
  'erase.code.label': 'Codice ricevuto',
  'erase.code.empty': 'Inserisci il codice che hai ricevuto.',
  'erase.code.wrong': 'Codice errato o scaduto. Controllalo e riprova.',
  'erase.code.submit': 'Conferma eliminazione',
  'erase.code.confirming': 'Conferma in corso…',
  'erase.code.change': 'Usa un altro numero',
  'erase.done.title': 'Richiesta ricevuta',
  'erase.done.body': 'Abbiamo ricevuto la tua richiesta. Se esiste un account Vello con questo numero, verrà eliminato; i dati che la legge obbliga a conservare (come corse e pagamenti) restano pseudonimizzati per il periodo previsto.',
  'erase.done.home': 'Torna alla pagina iniziale',
  'erase.error.rateLimited': 'Troppi tentativi. Riprova più tardi.',
  'erase.error.challenge': 'Non è stato possibile inviare il codice ora. Riprova più tardi.',
  'erase.error.notServed': 'Non inviamo ancora codici ai numeri di questo paese.',
  'erase.error.unavailable': 'Non è stato possibile completare ora. Riprova tra poco.',
  // D440: il link pubblico del viaggio e i documenti legali.
  'site.trip.loading': 'Caricamento…',
  'site.trip.state.waiting': 'Ricerca di un autista',
  'site.trip.state.onTheWay': 'L’autista sta arrivando al punto di partenza',
  'site.trip.state.arrived': 'L’autista è arrivato al punto di partenza',
  'site.trip.state.onTrip': 'In viaggio',
  'site.trip.driver': 'Autista: {name}',
  'site.trip.mapLabel': 'Mappa con l’ultima posizione nota del veicolo',
  'site.trip.mapUnavailable': 'La mappa non è disponibile su questo dispositivo. Lo stato del viaggio continua ad aggiornarsi qui.',
  'site.trip.updatedAt': 'Aggiornato alle {time}',
  'site.trip.noPosition': 'La posizione del veicolo non è disponibile ora.',
  'site.trip.privacy': 'Questo link mostra il veicolo, il percorso e la destinazione. Non mostra il nome né il telefono di chi ha richiesto il viaggio, né l’indirizzo di partenza. Smette di funzionare quando il viaggio termina.',
  'site.trip.unavailable': 'Non è stato possibile caricare il viaggio ora. Riproveremo.',
  'site.trip.ended.notFound': 'Questo link non esiste.',
  'site.trip.ended.revoked': 'Il passeggero ha smesso di condividere questo viaggio.',
  'site.trip.ended.expired': 'Questo link è scaduto.',
  'site.trip.ended.tripEnded': 'Questo viaggio è terminato.',
  'site.trip.ended.cancelled': 'Questa corsa è stata annullata.',
  'site.trip.eta': 'Arriva tra circa {minutes} min',
  'site.legal.version': 'Versione {version}',
  'site.legal.notPublished': 'Questo documento non è ancora stato pubblicato.',
};

export const SITE_EN_CATALOG: MessageCatalog = { locale: 'en-US', messages: EN };
export const SITE_PT_CATALOG: MessageCatalog = { locale: 'pt', messages: PT };
export const SITE_ES_CATALOG: MessageCatalog = { locale: 'es', messages: ES };
export const SITE_IT_CATALOG: MessageCatalog = { locale: 'it', messages: IT };

const CATALOGS: Readonly<Record<string, MessageCatalog>> = {
  en: SITE_EN_CATALOG,
  pt: SITE_PT_CATALOG,
  es: SITE_ES_CATALOG,
  it: SITE_IT_CATALOG,
};

/**
 * Pick the UI catalog for a resolved locale by its primary language subtag — `pt`, `pt-PT`, any `pt-<region>`
 * pick Portuguese (the region subtag is ignored, `Country != Locale`), `es`/`it`/`en` likewise; falls back to
 * en-US when none of the base languages matches.
 */
export function selectSiteCatalog(localeTag: string | undefined): MessageCatalog {
  const lang = (localeTag ?? '').toLowerCase().split('-')[0];
  return CATALOGS[lang] ?? SITE_EN_CATALOG;
}

/**
 * The localized URL slugs of the site's pages (X-03, D439), from every base-language catalog — any of them reaches the
 * page, so a link shared in another language still works. The page id stays neutral; the words are catalog copy.
 */
export function sitePageSlugs(): SitePageSlugs {
  const out: Partial<Record<SiteInnerPage, readonly string[]>> = {};
  for (const page of SITE_INNER_PAGES) {
    const words = new Set<string>();
    for (const catalog of Object.values(CATALOGS)) {
      const slug = catalog.messages[pageSlugId(page)];
      if (typeof slug === 'string' && slug !== '') words.add(slug);
    }
    out[page] = [...words];
  }
  return out;
}

/** The catalog id of a page's URL slug (`sign-in` → `site.route.signIn`). */
export function pageSlugId(page: SiteInnerPage): string {
  return `site.route.${page.replace(/-([a-z])/g, (_, c: string) => c.toUpperCase())}`;
}

export type Copy =(id: string, vars?: Readonly<Record<string, string | number>>) => string;

/** Bind a catalog into a `t(id)` the components call. */
export function createCopy(catalog: MessageCatalog): Copy {
  return (id, vars) => translate(catalog, id, vars).text;
}
